import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';

const AttendanceTable = ({
  attendanceData,
  totalRecordCount,
  sortConfig,
  onSort,
  onViewDetails,
  onAddMissingLog,
  onAddRemark,
  onApproveReject,
  onAddAttendanceEntry,
  canAddAttendanceEntry,
  canAddMissingLog
}) => {
  const { t } = useTranslation();

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'present':
        return 'bg-green-100 text-green-800';
      case 'late':
        return 'bg-yellow-100 text-yellow-800';
      case 'absent':
        return 'bg-red-100 text-red-800';
      case 'partial':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time;
  };

  const formatHours = (hours) => {
    if (hours === null || hours === undefined) return '-';
    const n = Number(hours);
    if (!Number.isFinite(n) || n === 0) return '-';

    const sign = n < 0 ? '-' : '';
    const totalMinutes = Math.round(Math.abs(n) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    if (h > 0 && m > 0) return `${sign}${h}h ${m}m`;
    if (h > 0) return `${sign}${h}h`;
    return `${sign}${m}m`;
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={16} className="text-primary" />
      : <Icon name="ArrowDown" size={16} className="text-primary" />;
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="attendance-table">
      {/* Table Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <span className="inline-flex items-center gap-2">
                <Icon name="Users" size={20} />
                {t('Team Attendance Records')}
              </span>
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t('Manage and track individual attendance records for your team')}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              {attendanceData?.length} {t('records found')}
            </div>
            {canAddAttendanceEntry ? (
              <button
                onClick={onAddAttendanceEntry}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name="Plus" size={16} />
                <span>{t('Add Attendance Entry')}</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {/* Table Container */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              {/* Employee Column */}
              <th 
                className="px-6 py-4 text-start text-sm font-medium text-foreground cursor-pointer hover:bg-muted/50"
                onClick={() => onSort('employee')}
              >
                <div className="flex items-center gap-2">
                  <span>{t('Employee')}</span>
                  {getSortIcon('employee')}
                </div>
              </th>

              {/* Date Column */}
              <th 
                className="px-6 py-4 text-start text-sm font-medium text-foreground cursor-pointer hover:bg-muted/50"
                onClick={() => onSort('date')}
              >
                <div className="flex items-center gap-2">
                  <span>{t('Date')}</span>
                  {getSortIcon('date')}
                </div>
              </th>

              {/* Clock In Column */}
              <th 
                className="px-6 py-4 text-start text-sm font-medium text-foreground cursor-pointer hover:bg-muted/50"
                onClick={() => onSort('clockIn')}
              >
                <div className="flex items-center gap-2">
                  <span>{t('In')}</span>
                  {getSortIcon('clockIn')}
                </div>
              </th>

              {/* Clock Out Column */}
              <th 
                className="px-6 py-4 text-start text-sm font-medium text-foreground cursor-pointer hover:bg-muted/50"
                onClick={() => onSort('clockOut')}
              >
                <div className="flex items-center gap-2">
                  <span>{t('Out')}</span>
                  {getSortIcon('clockOut')}
                </div>
              </th>

              {/* Hours Column */}
              <th 
                className="px-6 py-4 text-start text-sm font-medium text-foreground cursor-pointer hover:bg-muted/50"
                onClick={() => onSort('totalHours')}
              >
                <div className="flex items-center gap-2">
                  <span>{t('Hours')}</span>
                  {getSortIcon('totalHours')}
                </div>
              </th>

              {/* Overtime Column */}
              <th
                className="px-6 py-4 text-start text-sm font-medium text-foreground cursor-pointer hover:bg-muted/50"
                onClick={() => onSort('overtimeHours')}
              >
                <div className="flex items-center gap-2">
                  <span>{t('Overtime')}</span>
                  {getSortIcon('overtimeHours')}
                </div>
              </th>

              {/* Status Column */}
              <th 
                className="px-6 py-4 text-start text-sm font-medium text-foreground cursor-pointer hover:bg-muted/50"
                onClick={() => onSort('status')}
              >
                <div className="flex items-center gap-2">
                  <span>{t('Status')}</span>
                  {getSortIcon('status')}
                </div>
              </th>

              {/* Remarks Column */}
              <th className="px-6 py-4 text-start text-sm font-medium text-foreground">
                {t('Remarks')}
              </th>

              {/* Actions Column */}
              <th className="px-6 py-4 text-center text-sm font-medium text-foreground">
                {t('Actions')}
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {attendanceData?.map((record) => (
              <tr key={record?.id} className="hover:bg-muted/20 transition-colors">
                {/* Employee Info */}
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {record?.employee}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {record?.employeeTitle || '-'} • {record?.department}
                    </div>
                  </div>
                </td>

                {/* Date */}
                <td className="px-6 py-4">
                  <div className="text-sm text-foreground">
                    {new Date(record?.date)?.toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(record?.date)?.toLocaleDateString(undefined, { weekday: 'short' })}
                  </div>
                </td>

                {/* Clock In */}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Icon name="LogIn" size={14} className="text-green-600" />
                    <span className="text-sm text-foreground">{formatTime(record?.clockIn)}</span>
                  </div>
                </td>

                {/* Clock Out */}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Icon name="LogOut" size={14} className="text-red-600" />
                    <span className="text-sm text-foreground">{formatTime(record?.clockOut)}</span>
                  </div>
                </td>

                {/* Total Hours */}
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-foreground">
                    {formatHours(record?.totalHours)}
                  </span>
                </td>

                {/* Overtime Hours */}
                <td className="px-6 py-4">
                  {Number(record?.overtimeHours) > 0 ? (
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-600">
                      <Icon name="Timer" size={14} />
                      {formatHours(record?.overtimeHours)}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </td>

                {/* Status */}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(record?.status)}`}>
                      {t(record?.status || 'Unknown')}
                    </span>
                    {record?.correctionPending && (
                      <div className="w-2 h-2 bg-orange-500 rounded-full" title={t('Correction Pending')}></div>
                    )}
                  </div>
                </td>

                {/* Remarks */}
                <td className="px-6 py-4">
                  <div className="text-sm text-muted-foreground max-w-40 truncate" title={record?.remarks}>
                    {record?.remarks || '-'}
                  </div>
                </td>

                {/* Actions */}
                <td className="px-6 py-4">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => onViewDetails?.(record)}
                      className="p-1 text-slate-700 hover:bg-slate-100 rounded transition-colors"
                      title={t('View')}
                    >
                      <Icon name="Eye" size={16} />
                    </button>

                    {record?.correctionPending && (
                      <button
                        onClick={() => onApproveReject(record)}
                        className="p-1 text-amber-600 hover:bg-amber-100 rounded transition-colors"
                        title={t('Approve/Reject')}
                      >
                        <Icon name="CheckCircle2" size={16} />
                      </button>
                    )}
                    
                    {canAddMissingLog && (record?.clockIn === '-' || record?.clockOut === '-') && (
                      <button
                        onClick={() => onAddMissingLog(record)}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                        title={t('Add Missing Log')}
                      >
                        <Icon name="Plus" size={16} />
                      </button>
                    )}
                    
                    <button
                      onClick={() => onAddRemark(record)}
                      className="p-1 text-purple-600 hover:bg-purple-100 rounded transition-colors"
                      title={t('Add Remark')}
                    >
                      <Icon name="MessageCircle" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Empty State */}
        {attendanceData?.length === 0 && (
          <div className="text-center py-16" data-testid="attendance-empty-state">
            <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground/40" />
            {totalRecordCount === 0 ? (
              <>
                <h3 className="text-lg font-medium text-foreground mb-2">{t('No attendance records found')}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {t('No employees have clocked in during this period. Employees will appear here once they start clocking in.')}
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-foreground mb-2">{t('No records match your filters')}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {t('Try adjusting the date range, department, or status filters to see attendance data.')}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceTable;